import React from 'react';
import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { useInstanceId, variantSwitch } from '@adsk/alloy-react-helpers';
import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';
import Tooltip, {
  InfoPopover,
  InfoPopoverProps,
} from '@adsk/alloy-react-tooltip';

const INFO_POPOVER_ICON_STYLE = {
  marginLeft: 5,
  marginTop: theme.spacing.XXXS,
  height: 16,
};

const FORM_FIELD_ERROR_VARIANTS = {
  CAPTION: 'caption',
  TOOLTIP: 'tooltip',
};

type DefaultInfoPopoverProps = Omit<InfoPopoverProps, 'message'>;

const DEFAULT_INFO_POPOVER_PROPS: DefaultInfoPopoverProps = {
  variant: InfoPopover.VARIANTS.LIGHT,
  style: INFO_POPOVER_ICON_STYLE,
  placement: InfoPopover.PLACEMENTS.TOP,
};

const getTooltipTargetProps = () => ({ shouldWrapChildren: false });

type ErrorVariants = typeof FORM_FIELD_ERROR_VARIANTS;
export type ErrorVariant = ErrorVariants[keyof ErrorVariants];

const LABEL_VARIANTS = {
  SIDE: 'side',
  TOP: 'top',
};

type LabelVariants = typeof LABEL_VARIANTS;
export type LabelVariant = LabelVariants[keyof LabelVariants];

export type RenderInfoPopover = (
  defaultProps: DefaultInfoPopoverProps,
) => ReactNode;

export type FormFieldProps = StylableComponent<HTMLDivElement> & {
  error?: ReactNode;
  errorVariant?: ErrorVariant;
  label?: ReactNode;
  labelVariant?: LabelVariant;
  required?: boolean;
  renderInfoPopover?: RenderInfoPopover;
};

/**
 * A FormField component which provides error message displaying.
 */
const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      className,
      style,
      error,
      errorVariant = FORM_FIELD_ERROR_VARIANTS.CAPTION,
      label,
      labelVariant = LABEL_VARIANTS.SIDE,
      required,
      children,
      renderInfoPopover,
      ...props
    },
    ref,
  ) => {
    const id = useInstanceId();

    const labelId = `FormFieldLabel${id}`;
    const errorId = `FormFieldError${label}`;

    return (
      <div
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            width: '100%',
          },
          variantSwitch({
            statement: labelVariant,
            variants: {
              [LABEL_VARIANTS.TOP]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
              },
            },
          }),
          style,
        ]}
      >
        {label && (
          <label
            id={labelId}
            data-testid="FormField__label"
            css={[
              {
                display: 'flex',
                color: theme.tokens.colors.text.secondary.value,
              },
              variantSwitch({
                statement: labelVariant,
                variants: {
                  [LABEL_VARIANTS.SIDE]: {
                    marginRight: theme.spacing.XXS,
                    marginTop: theme.spacing.XS,
                    ...theme.typography.labelMedium,
                  },
                  [LABEL_VARIANTS.TOP]: {
                    marginBottom: theme.spacing.XS,
                    ...theme.typography.labelSmall,
                  },
                },
              }),
            ]}
          >
            <span>{label}</span>
            {required && (
              <span
                css={[
                  {
                    color: theme.tokens.colors.icon.error.value,
                    marginLeft: '3px',
                  },
                ]}
              >
                *
              </span>
            )}
            {renderInfoPopover?.(DEFAULT_INFO_POPOVER_PROPS)}
          </label>
        )}
        <div
          css={[
            {
              display: 'flex',
              width: '100%',
              '&>*': {
                flex: '1 1 auto',
              },
            },
            variantSwitch({
              statement: errorVariant,
              variants: {
                [FORM_FIELD_ERROR_VARIANTS.CAPTION]: {
                  flexDirection: 'column',
                },
                [FORM_FIELD_ERROR_VARIANTS.TOOLTIP]: {
                  alignItems: 'center',
                },
              },
            }),
          ]}
        >
          <div>
            {React.isValidElement(children)
              ? React.cloneElement<ComponentPropsWithRef<ElementType>>(
                  children,
                  {
                    'aria-labelledby': labelId,
                    'aria-invalid': error ? true : undefined,
                    'aria-errormessage': error ? errorId : undefined,
                    'aria-required': required ? true : undefined,
                    required,
                  },
                )
              : children}
          </div>
          {error && errorVariant === FORM_FIELD_ERROR_VARIANTS.TOOLTIP && (
            <div
              id={errorId}
              role="alert"
              data-testid="FormField__toolTip"
              css={[
                {
                  color: theme.tokens.colors.icon.error.value,
                  flex: '0 0 auto',
                  marginLeft: theme.spacing.XXS,
                  display: 'flex',
                  alignItems: 'center',
                },
              ]}
            >
              <Tooltip
                getTargetProps={getTooltipTargetProps}
                content={error}
                placement={Tooltip.PLACEMENTS.TOP}
              >
                <AlertWarningFilledIcon size={16} />
              </Tooltip>
            </div>
          )}
          {error && errorVariant === FORM_FIELD_ERROR_VARIANTS.CAPTION && (
            <div
              id={errorId}
              role="alert"
              data-testid="FormField__error"
              css={[
                {
                  display: 'flex',
                  margin: `${theme.spacing.XXS}px 0`,
                },
              ]}
            >
              <AlertWarningFilledIcon
                size={16}
                style={{
                  flexShrink: 0,
                  marginTop: theme.spacing.XXXS,
                  color: theme.tokens.colors.icon.error.value,
                }}
              />
              <span
                css={[
                  {
                    fontSize: 12,
                    color: theme.tokens.colors.text.error.value,
                    marginLeft: theme.spacing.XS,
                  },
                ]}
              >
                {error}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  },
);

FormField.displayName = 'FormField';

FormField.propTypes = {
  /** Child input component(s) */
  children: PropTypes.any,
  /** The error message to display */
  error: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The error variant */
  errorVariant: PropTypes.oneOf(Object.values(FORM_FIELD_ERROR_VARIANTS)),
  /** The text for the label.  This text is also used to set the aria-label prop on the provided children */
  label: PropTypes.any,
  /** The label variant */
  labelVariant: PropTypes.oneOf(Object.values(LABEL_VARIANTS)),
  /** Field is required */
  required: PropTypes.bool,
  /** Render function for info popover that receives default props and renders InfoPopover */
  renderInfoPopover: PropTypes.func,
};

export default Object.assign(FormField, {
  ERROR_VARIANTS: FORM_FIELD_ERROR_VARIANTS,
  LABEL_VARIANTS,
  INFO_POPOVER_VARIANTS: InfoPopover.VARIANTS,
});
